




































































































import { Component, Vue, Ref, Watch, Prop } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";

import User from "@/data/models/User";
import {
  createSharedEmailAddressInfo,
  deleteSharedEmailAddressInfoForUser,
  getSharedEmailAddressInfo,
  updatePasswordOfSharedEmailAddress,
  updateHomeClientOfSharedEmailAddress,
} from "@/data/repository/SharedEmailAddressRepository";
import { SharedEmailAddressInfo, SharedEmailAddressUserInfo } from "@/data/models/SharedEmailAddressInfo";
import { getClientId, getClientInfo } from "@/data/repository/ClientRepository";
import ResetPasswordDialogComponent from "../common/ResetPasswordDialog.vue";
import { getActiveUser, isEmailAlreadyUsed, storeUserUpdate } from "@/data/repository/UserRepository";
import JoinSharedEmailAddressComponent from "../users/sharedemailaddress/JoinSharedEmailAddress.vue";
import { COLOR_BUTTON, COLOR_BUTTON_GREEN } from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import SetNewPasswordComponent from "@/components/users/SetNewPassword.vue";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "reset-password-dialog": ResetPasswordDialogComponent,
    "set-new-password": SetNewPasswordComponent,
    "join-shared-email-address": JoinSharedEmailAddressComponent,
  },
})
export default class SharedEmailAddressFormComponent extends Vue {
  @Prop({ default: false })
  showSharedEmailAddressOptions!: boolean;

  isLoading = false;

  clients: SharedEmailAddressUserInfo[] = [];

  newEmailAddress = ""
  emailError: Array<string> = [];

  newPassword = "";
  newPasswordRepeated = "";

  initialHomeClient = "";
  selectedHomeClient = "";

  clickedAny = false;
  clickedChangePassword = false;
  clickedChangeHomeClient = false;
  clickedQuitSharing = false;

  newPasswordError: string[] = [];
  newPasswordRepeatedError: string[] = [];

  showNewPassword = false;
  showNewPasswordRepeated = false;

  showQuitSharing = false;
  showChangeHomeClient = false;

  user!: User;
  hasUserSharedEmailAddress = false;

  sharedEmailAddressInfo?: SharedEmailAddressInfo;
  numberOfSharedUsers = 0;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  @Ref("resetPw")
  resetPasswordDialogComponent!: ResetPasswordDialogComponent;

  @Ref("setNewPassword")
  setNewPassword!: SetNewPasswordComponent;

  @Ref("joinSharedEmailAddress")
  joinSharedEmailAddressComponent!: JoinSharedEmailAddressComponent;

  @Watch("newPassword")
  onNewPasswordChange() {
    this.newPasswordError = [];
  }

  @Watch("newPasswordRepeated")
  onNewPasswordRepeatedChange() {
    this.newPasswordRepeatedError = [];
  }

  resolve?: (value: boolean) => void = undefined;
  reject?: (reason: string) => void = undefined;

  get activeUser() {
    return getActiveUser();
  }

  /** The option's fill for the active theme: the palette green and the accent red in dark mode. */
  getOptionFill(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    const color = this.getOptionColor(sharedEmailAddressUserInfo);

    return color ? themedFill(color) : color;
  }

  /** The option's label class: white in light mode; dark on the palette green in dark mode. */
  getOptionTextClass(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    const color = this.getOptionColor(sharedEmailAddressUserInfo);

    return color ? themedFillTextClass(color, "white-font") : "white-font";
  }

  getOptionColor(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    return sharedEmailAddressUserInfo.clientId === this.selectedHomeClient ? COLOR_BUTTON_GREEN : COLOR_BUTTON;
  }

  getName(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    return `${sharedEmailAddressUserInfo.clientName}`;
  }

  async onHomeClientChanged(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    if (sharedEmailAddressUserInfo.clientId === this.selectedHomeClient) {
      return;
    }

    this.selectedHomeClient = sharedEmailAddressUserInfo.clientId;
    const selectedHomeClientName = sharedEmailAddressUserInfo.clientName;

    const changeConfirmed = await this.confirmationDialogComponent.show({
      title: this.$t("sharedEmailAddressForm.confirmChangeHomeClientTitle").toString(),
      message: this.$t(
        "sharedEmailAddressForm.confirmChangeHomeClient",
        [selectedHomeClientName]
      ).toString(),
      acceptMessage: this.$t("common.change").toString(),
    });

    if (changeConfirmed) {
      await updateHomeClientOfSharedEmailAddress(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.sharedEmailAddressInfo?.id!!,
        sharedEmailAddressUserInfo.clientId,
        sharedEmailAddressUserInfo.userId,
        this.activeUser?.id ?? "",
      )

      this.isLoading = false;
      this.baseDialogComponent.isShowing = false;
      this.resolve && this.resolve(true);
    }
    else {
      this.selectedHomeClient = this.initialHomeClient;
    }
  }

  resetDialog() {
    this.clickedAny = false;
    this.clickedChangePassword = false;
    this.clickedChangeHomeClient = false;
    this.clickedQuitSharing = false;

    this.newPassword = "";
    this.newPasswordRepeated = "";

    this.newPasswordError = [];
    this.newPasswordRepeatedError = [];

    this.newEmailAddress = "";
    this.emailError = [];

    return 
  }

  resetDialogParameters() {
    this.baseDialogComponent.title = this.$t("settings.sharedEmailAddressOptions.title").toString();
    
    this.baseDialogComponent.positiveButtonText = "";
    this.baseDialogComponent.negativeButtonText = this.$t("common.abort").toString();
    
    this.baseDialogComponent.persistent = false;
    this.baseDialogComponent.showPositiveButton = false;
  }

  async show(user: User) {
    this.resetDialog();

    this.user = user;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.hasUserSharedEmailAddress = !!user.sharedEmailAddressId;

    if (user.sharedEmailAddressId) {
      const sharedEmailAddressInfo = await getSharedEmailAddressInfo(user.sharedEmailAddressId);

      if (sharedEmailAddressInfo) {
        this.sharedEmailAddressInfo = sharedEmailAddressInfo;
        this.clients = 
          Object.values(sharedEmailAddressInfo.userInfo);
        this.numberOfSharedUsers = Object.keys(sharedEmailAddressInfo.userInfo).length;
        const numberOfClients = sharedEmailAddressInfo.clientIds.length;

        this.initialHomeClient = sharedEmailAddressInfo.sourceClientId;
        this.selectedHomeClient = this.initialHomeClient;

        if (getClientId() === sharedEmailAddressInfo.sourceClientId && this.numberOfSharedUsers > 1) {
          this.showQuitSharing = false;
        }
        else {
          this.showQuitSharing = true;
        }

        if (numberOfClients > 1) {
          this.showChangeHomeClient = true;
        }
      }
    }

    const positiveButtonText = 
      user.sharedEmailAddressId && 
      this.showSharedEmailAddressOptions
        ? null
        : this.$t(
            user.sharedEmailAddressId
              ? "resetPassword.setNewEmailSharingPassword"
              : "common.save"
          ).toString()

    this.baseDialogComponent.show({
      title: this.showSharedEmailAddressOptions
        ? this.$t("settings.sharedEmailAddressOptions.title").toString()
        : this.$t("settings.sharedEmailAddress.title").toString(),
      positiveButtonText: positiveButtonText,
      negativeButtonText: this.$t("common.abort").toString(),
      closeOnSave: false,
    });

    if (this.hasUserSharedEmailAddress && !this.clickedAny) {
      this.resetDialogParameters();
    }

    return new Promise<boolean>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  toggleShowNewPassword() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleShowNewPasswordRepeated() {
    this.showNewPasswordRepeated = !this.showNewPasswordRepeated;
  }

  onBack() {
    if (!this.showSharedEmailAddressOptions || !this.clickedAny) {
      this.baseDialogComponent.isShowing = false;
    }

    this.resetDialog();
    this.resetDialogParameters();
  }

  onChangePassword() {
    this.clickedAny = true;
    this.clickedChangePassword = true;

    this.clickedChangeHomeClient = false;
    this.clickedQuitSharing = false;

    this.baseDialogComponent.title = this.$t("sharedEmailAddressForm.changePasswordTitle").toString();
    
    this.baseDialogComponent.positiveButtonText = this.$t("resetPassword.setNewEmailSharingPassword").toString();
    this.baseDialogComponent.negativeButtonText = this.$t("common.back").toString();

    this.baseDialogComponent.persistent = true;

    return;
  }

  onChangeHomeClient() {
    this.clickedAny = true;
    this.clickedChangeHomeClient = true;

    this.clickedChangePassword = false;
    this.clickedQuitSharing = false;

    this.baseDialogComponent.title = this.$t("sharedEmailAddressForm.changeHomeClientTitle").toString();
    
    this.baseDialogComponent.negativeButtonText = this.$t("common.back").toString();

    this.baseDialogComponent.persistent = true;

    return;
  }

  async onResetPassword() {
    this.baseDialogComponent.isShowing = false;

    const user = this.user;

    user.emailAddress = this.newEmailAddress;
    user.sharedEmailAddressId = null;

    this.$emit("isLoading", true);
  
    await storeUserUpdate(
      this.user.id,
      user,
      undefined
    );
    
    await this.setNewPassword.show(
      user,
      false,
      true
    );

    this.$emit("isLoading", false);
  }

  async onQuitSharing() {
    if (this.numberOfSharedUsers === 1) {
      this.isLoading = false;
      this.baseDialogComponent.isShowing = false;
      const confirmedDeletion = await this.confirmationDialogComponent.show({
        title: this.$t("users.deleteSharedEmailAddressDialog.title").toString(),
        message: this.$t(
          "users.deleteSharedEmailAddressDialog.message"
        ).toString(),
        acceptMessage: this.$t("common.delete").toString(),
      });

      if (!confirmedDeletion) {
        this.baseDialogComponent.isShowing = true;
        return;
      }

      await deleteSharedEmailAddressInfoForUser(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.user.sharedEmailAddressId!!,
        this.user.id
      );
      
      this.newEmailAddress = this.user.emailAddress ?? "";
      await this.onResetPassword();

      this.resolve && this.resolve(true);
    }
    else {
      this.clickedAny = true;
      this.clickedQuitSharing = true;

      this.clickedChangePassword = false;
      this.clickedChangeHomeClient = false;

      this.baseDialogComponent.title = this.$t("sharedEmailAddressForm.quitSharingTitle").toString();
      
      this.baseDialogComponent.positiveButtonText = this.$t("common.confirm").toString();
      this.baseDialogComponent.negativeButtonText = this.$t("common.back").toString();

      this.baseDialogComponent.persistent = true;
    }

    return;
  }

  async onSave() {
    if (this.clickedQuitSharing) {
      if (this.newEmailAddress === "") {
        this.isLoading = false;
        this.baseDialogComponent.isShowing = false;

        await deleteSharedEmailAddressInfoForUser(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this.user.sharedEmailAddressId!!,
          this.user.id
        );
        
        this.newEmailAddress = "";
        await this.onResetPassword();

        this.resolve && this.resolve(true);
      }
      else {
        const result = await isEmailAlreadyUsed(this.newEmailAddress, this.user.id);
        this.baseDialogComponent.isShowing = false;

        if (result.isEmailAlreadyUsed) {
          if (result.sharedEmailAddressId) {
            this.isLoading = false;

            const didJoinSharedEmailAddress = await this.joinSharedEmailAddressComponent.show(
              {
                userId: this.user.id,
                userNickname: this.user.nickname,
                sharedEmailAddressId: result.sharedEmailAddressId,
                emailAddress: this.newEmailAddress,
                clientName: getClientInfo()?.companyName ?? "",
                createdBy: this.activeUser?.id ?? "",
              }
            );

            if (!didJoinSharedEmailAddress) {
              this.baseDialogComponent.isShowing = true;
              return;
            }

            await this.onResetPassword();
            this.resolve && this.resolve(true);
          } 
          else {
            this.emailError = [this.$t("users.error.emailAlreadyUsed").toString()];
            this.baseDialogComponent.isShowing = true;
            return;
          }
        }

        await deleteSharedEmailAddressInfoForUser(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this.user.sharedEmailAddressId!!,
          this.user.id
        );

        await this.onResetPassword();
        this.resolve && this.resolve(true);
      }
    }
    else {
      const newPassword = this.newPassword;
      const newPasswordRepeated = this.newPasswordRepeated;

      this.isLoading = true;

      if (newPassword.length < 8) {
        this.newPasswordError = [
          this.$t("resetPassword.errorMinEmailSharingPasswordLength").toString(),
        ];
        this.isLoading = false;
        return;
      }

      if (newPassword != newPasswordRepeated) {
        this.newPasswordRepeatedError = [
          this.$t("resetPassword.errorEmailSharingPasswordNoMatch").toString(),
        ];
        this.isLoading = false;
        return;
      }

      if (this.user.sharedEmailAddressId) {
        await updatePasswordOfSharedEmailAddress(
          this.user.sharedEmailAddressId,
          newPassword
        );
      } else {
        await createSharedEmailAddressInfo(this.user, newPassword);
      }

      this.isLoading = false;
      this.baseDialogComponent.isShowing = false;
      this.resolve && this.resolve(true);
    }
  }
}
